import { ReactElement } from "react";
import {
	AiMappingStepConfigurationSaveModel,
	IntegrationFlowValidationResultAiSampleModel
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { DateString, FormItem } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { IInputAttachable, IObjectInputAttachable } from "client/Forms/attachable";
import { isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ResultMapperFormResult } from "./ResultMapperFormResult";
import { ExpressionTextInput } from "./ExpressionTextInput";

export type AiFormProps = {
	attachable: IObjectInputAttachable<AiMappingStepConfigurationSaveModel>;
	isReadOnly: boolean;
	timeZone: TimeZone;
	aiSample: OptionalModel<IntegrationFlowValidationResultAiSampleModel> | null;
};

export const AiForm = (props: AiFormProps): ReactElement => {
	const {
		attachable,
		isReadOnly,
		aiSample,
		timeZone
	} = props;

	const contentExpressionHandler: IInputAttachable<string> = attachable.for("contentExpression");
	const idExpressionHandler: IInputAttachable<string> = attachable.for("idExpression");
	const titleExpressionHandler: IInputAttachable<string> = attachable.for("titleExpression");
	const linkExpressionHandler: IInputAttachable<string> = attachable.for("linkExpression");
	const dateExpressionHandler: IInputAttachable<string> = attachable.for("dateExpression");
	const hasResult: boolean = isNotNil(aiSample)
		&& aiSample.isSet;

	const aiSampleValue: IntegrationFlowValidationResultAiSampleModel | null = isNotNil(aiSample) && aiSample.isSet
		? aiSample.value
		: null;

	return (
		<>
			<FormItem
				label={localize("_Content")}
				isRequired
			>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={contentExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={contentExpressionHandler}
					hasResult={hasResult}
					resultValue={aiSampleValue?.content}
				/>
			</FormItem>
			<FormItem label={localize("_Id")}>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={idExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={idExpressionHandler}
					hasResult={hasResult}
					resultValue={aiSampleValue?.id}
				/>
			</FormItem>
			<FormItem label={localize("_Title")}>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={titleExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={titleExpressionHandler}
					hasResult={hasResult}
					resultValue={aiSampleValue?.title}
				/>
			</FormItem>
			<FormItem label={localize("_Link")}>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={linkExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={linkExpressionHandler}
					hasResult={hasResult}
					resultValue={aiSampleValue?.link}
				/>
			</FormItem>
			<FormItem label={localize("_DateCreatedModified")}>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={dateExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={dateExpressionHandler}
					hasResult={hasResult}
					resultValue={aiSampleValue?.dateTime}
				>
					{aiSampleValue?.dateTime && (
						<DateString
							value={aiSampleValue.dateTime}
							timezone={timeZone}
							format="RelativeDayWithTime"
						/>
					)}
				</ResultMapperFormResult>
			</FormItem>
		</>
	);
};
AiForm.displayName = "AiForm";